<template>
    <div class="supplier-view">
      <div class="page-header">
        <h2>매입처 관리</h2>
      <el-button size="small" @click="helpVisible = true">도움말</el-button>
      <HelpPanel v-model="helpVisible" :content-path="'screens/supplier-list'" />
        <div class="header-actions">
          <el-button v-if="isSupplierManager" type="primary" @click="openDialog('add')">+ 새 매입처 추가</el-button>
          <el-button v-if="isSupplierManager" @click="downloadExcel" :loading="excelDownloading">엑셀 다운로드</el-button>
          <el-button v-if="isSupplierManager" @click="openExcelDialog">엑셀 업로드</el-button>
        </div>
      </div>
      <el-input
        v-model="searchQuery"
        placeholder="거래처 이름을 입력하세요"
        @input="filterSuppliers"
      />

      <!-- 매입처 목록 -->
      <!-- ag-grid 테이블 -->
      <div class="ag-grid-container">
        <div class="ag-theme-alpine" style="height: 100%; width: 100%; margin-top: 20px;">
          <ag-grid-vue
              ref="gridRef"
              :rowData="suppliers"
              :columnDefs="columnDefs"
              :defaultColDef="defaultColDef"
              :pagination="true"
              :paginationPageSize="paginationPageSize"
              :paginationPageSizeSelector="[10, 20, 50, 100]"
              @paginationChanged="onPaginationChanged"
              :rowSelection="{ type: 'single' }"
              @rowClicked="onRowClicked"
              style="width: 100%; height: 100%;"
          ></ag-grid-vue>
      </div>
    </div>

    <!-- 매입처 추가/수정 다이얼로그 -->
      <SupplierDialog
        v-if="supplierDialogVisible"
        :supplier="selectedSupplier"
        @save="updateSupplier"
        @close="handleDialogClose"
      />

      <!-- 엑셀 업로드 다이얼로그 (검증 → 반영, 2026-07-17) -->
      <el-dialog v-model="excelDialogVisible" title="매입처 엑셀 업로드" width="600px"
                 :close-on-click-modal="false" destroy-on-close>
        <div style="font-size:12px; color:#909399; margin-bottom:6px">
          [엑셀 다운로드] 서식 그대로 수정/행 추가 후 업로드하세요.
          ID → 사업자번호 → 이름 순으로 찾아 갱신하고, 못 찾으면 신규 등록됩니다.
          빈 칸은 변경되지 않습니다. 계좌·연락처는 상세 화면에서 관리합니다.
        </div>
        <input type="file" accept=".xlsx" @change="onExcelFileChange" style="margin: 10px 0;" />
        <template v-if="excelReport">
          <el-alert :type="excelReportHasError ? 'warning' : 'success'" :closable="false" show-icon
                    :title="excelReport.dry_run ? '검증 결과 (아직 반영 안 됨)' : '반영 완료'">
            <div>신규 {{ excelReport.suppliers.created.length }} ·
              수정 {{ excelReport.suppliers.updated.length }} ·
              오류 {{ excelReport.suppliers.errors.length }}</div>
          </el-alert>
          <div v-if="excelReport.suppliers.created.length"
               style="font-size:12px; color:#606266; margin-top:6px; word-break:break-all">
            신규: {{ excelReport.suppliers.created.join(', ') }}</div>
          <div v-if="excelReport.suppliers.updated.length"
               style="font-size:12px; color:#606266; margin-top:4px; word-break:break-all">
            수정: {{ excelReport.suppliers.updated.join(', ') }}</div>
          <div v-for="(err, i) in excelReport.suppliers.errors" :key="i"
               style="font-size:12px; color:#e6a23c; margin-top:4px">⚠ {{ err }}</div>
        </template>
        <template #footer>
          <el-button @click="excelDialogVisible = false">닫기</el-button>
          <el-button type="primary" plain :disabled="!excelFile" :loading="excelUploading"
                     @click="uploadExcel(false)">검증</el-button>
          <el-button type="primary"
                     :disabled="!excelFile || !excelReport || !excelReport.dry_run || excelReportHasError"
                     :loading="excelUploading" @click="uploadExcel(true)">반영</el-button>
        </template>
      </el-dialog>

    </div>
  </template>
  
  <script setup>
import HelpPanel from '@/components/help/HelpPanel.vue'
import { ref as _helpRef } from 'vue'
const helpVisible = _helpRef(false)
  import { ref, computed, onMounted } from "vue";
import { ElMessage } from "element-plus";
  import { AgGridVue } from "ag-grid-vue3";
  import axiosInstance from "@/api/axios"; // Axios 인스턴스 가져오기
  import SupplierDialog from "@/components/SupplierDialog.vue";
  import {formatBusinessNumber} from "@/utils/formatBusinessNumber"
  import { useUserStore } from "@/stores/user";



  // 상태 관리 변수
  const suppliers = ref([]); // 현재 페이지의 데이터
  const totalSuppliers = ref(0);//전체 데이터 수
  const currentPage = ref(1);//현재 페이지 번호
  const paginationPageSize= ref(10);//페이지 크기 
  const searchQuery = ref("");//검색어
  const supplierDialogVisible = ref(false);
  const selectedSupplier = ref(null); // 선택된 매입처


  const dialogTitle = ref(""); // 다이얼로그 제목

  const userStore = useUserStore();
  const isSupplierManager = userStore.isSupplierManager;
  
// ag-grid 컬럼 정의
const columnDefs = ref([
  
 { field: "supplier_id", headerName: "번호" },
  { field: "name", headerName: "공급업체명" },
  { field: "business_number", headerName: "사업자등록번호", 
    cellRenderer:(params)=>{
      return `<span>${formatBusinessNumber(params.value)}</span>`;
    }
  },
  { field: "representative_name", headerName: "대표자명" },
  { field: "is_individual", headerName: "개인", width: 90,
    cellRenderer: params => params.value ? "👤 개인" : "" },  // 프리랜서 (2026-07-20)
  { 
        field: "category_name", 
        headerName: "매입처 구분", 
  },  
  { field: "is_active", headerName: "활성여부", cellRenderer: params => params.value ? "활성" : "비활성" },  
]);

const defaultColDef = {
  //flex: 1,
  minWidth: 100, // ✅ 최소 너비 설정 (모바일에서도 텍스트가 잘리지 않도록)

  sortable: true,
  resizable: true,
};



// 서버에서 데이터 로드
// 서버 기본 페이지 크기(StandardPagination)로 전건을 한 번에 받아
// ag-grid 클라이언트 페이지네이션에 위임한다.
// page_size 를 명시 송신하면 서버가 그 값으로 잘라 보내므로 보내지 않는다.
const loadSuppliers = async (query = "") => {
  try {
    const response = await axiosInstance.get("/suppliers/", {
      params: {
        search: query,
      },
    });
    console.log(response.data)
    suppliers.value = response.data.results;
    console.log(suppliers.value)
    totalSuppliers.value = response.data.count;
  } catch (error) {
    console.error("데이터 로드 중 오류:", error);
  }
};

const updateSupplier = () => {
  filterSuppliers();
  //supplierDialogVisible.value = false; // 다이얼로그 닫기
};

// 다이얼로그 열기
const openDialog = (mode, supplier = null) => {
  console.log('opendialog',mode)
  if (mode === "add") {
    dialogTitle.value = "새 매입처 추가";
    selectedSupplier.value = { supplier_id: null, name: "",english_name:"", country: "",english_country:"", address:"",english_address:"",
                  business_number: '',category:"", category_name:"",representative_name: "",is_activate:true,
                  accounts: [],
                  contacts: [],
                 };
  } else if (mode === "edit") {
 
    dialogTitle.value = "매입처 수정";
    selectedSupplier.value = { ...supplier }; // 선택된 매입처 데이터 복사
  }
  
  console.log('supplierDialogVisible=',selectedSupplier.value)

  supplierDialogVisible.value = true;
  console.log('supplierDialogVisible=',supplierDialogVisible.value)
};

// 페이지 변경 처리 (클라이언트 페이지네이션이므로 서버 재요청 없음)
const onPaginationChanged = (event) => {
  if (!event?.api) return;
  currentPage.value = event.api.paginationGetCurrentPage() + 1;
};


const handleDialogClose = () => {
  console.log("모달이 닫혔습니다.");
  supplierDialogVisible.value = false; // 다이얼로그 닫기

  // 필요한 추가 동작 처리 (예: 상태 초기화)
};

// 행 클릭 처리
const onRowClicked = (event) => {
  openDialog("edit", event.data);
};

// 검색 필터
const filterSuppliers = () => {
  loadSuppliers(searchQuery.value);
};

// 컴포넌트 로드 시 매입처 데이터 가져오기
// 컴포넌트 로드 시 데이터 가져오기
// ── 엑셀 다운로드/업로드 (2026-07-17) ──
const excelDownloading = ref(false)
const excelDialogVisible = ref(false)
const excelUploading = ref(false)
const excelFile = ref(null)
const excelReport = ref(null)
const excelReportHasError = computed(() =>
  !!excelReport.value && excelReport.value.suppliers.errors.length > 0)

const downloadExcel = async () => {
  excelDownloading.value = true
  try {
    const res = await axiosInstance.get('/suppliers/excel-export/', { responseType: 'blob' })
    const url = URL.createObjectURL(res.data)
    const a = document.createElement('a')
    a.href = url
    a.download = `매입처_${new Date().toISOString().slice(0, 10)}.xlsx`
    a.click()
    URL.revokeObjectURL(url)
  } catch (e) {
    console.error('엑셀 다운로드 실패:', e)
    ElMessage.error('엑셀 다운로드에 실패했습니다')
  } finally {
    excelDownloading.value = false
  }
}
const openExcelDialog = () => {
  excelFile.value = null
  excelReport.value = null
  excelDialogVisible.value = true
}
const onExcelFileChange = (e) => {
  excelFile.value = e.target.files?.[0] || null
  excelReport.value = null
}
const uploadExcel = async (apply) => {
  if (!excelFile.value) return
  excelUploading.value = true
  try {
    const fd = new FormData()
    fd.append('file', excelFile.value)
    const res = await axiosInstance.post(
      `/suppliers/excel-import/${apply ? '?apply=1' : ''}`, fd,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    excelReport.value = res.data
    if (apply) {
      ElMessage.success('반영되었습니다')
      loadSuppliers()
    }
  } catch (e) {
    console.error('엑셀 업로드 실패:', e)
    ElMessage.error(e.response?.data?.detail || '엑셀 업로드에 실패했습니다')
  } finally {
    excelUploading.value = false
  }
}

onMounted(() => {  
  loadSuppliers();
});

  </script>
  
<!--
  ⚠ 과거 `.ag-theme-alpine { height:500px; overflow:auto !important }` 가 전역으로 새서
  이 화면 방문 후 모든 ag-grid 화면의 높이/스크롤 동작이 오염되던 누수 → 네임스페이스로 격리 (2026-06-11)
-->
<style>
  .supplier-view .ag-grid-container {
    width: 100%;
    height: 500px;
    overflow-x: auto;
  }

  .supplier-view .ag-theme-alpine {
    width: 100%;
    height: 100%;
  }
</style>